﻿import React, { useEffect, useState } from "react";
import { Intent, Icon } from '@blueprintjs/core';
import moment from "moment";
import axios from 'axios';
import PropTypes from 'prop-types';
import { useValidation } from "hooks/useValidation";
import { Breadcrumbs } from "components/navigation";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { useDirtyData } from 'hooks/useDirtyData';
import { ShowHide } from "components/layout";
import { NotificationToaster, AlertUnsavedData } from "components/notifications";
import { LinkButton, Button, ButtonSave } from "components/buttons";
import { AssetService, LocationService, UserService } from 'services';
import { FormSelect, FormTextInput, FormDateInput, FormLocationSelector, FormSuggest, FormPagedCheckboxList } from "components/form-fields";
import { HelperFunctions } from "helpers";
import assetTypeDisplay from "pages/asset/edit/AssetTypeDisplay.json";

export function AssetCreateSettings(
    {
        breadcrumbs = [],
        area = "MMF",
        businessArea,
        disabled,
        saving
    }
) {

    const assetModel = {
        registration: null,
        assetType: {
            subtypeId: null
        },
        chassisNumber: null,
        tankNo: null,
        serialNumber: null,
        detrNumber: null,
        assetLocation: {
            location: {},
            assetStatus: {},
            assetOwnership: {},
            startDate: null,
        },
        analysisRules: null,
        linkedVehicleId: null,
        relatedAssets: []
    };

    const [isValid, errors, validate] = useValidation();
    const [originalSettings, setOriginalSettings] = useState(assetModel);
    const [loading, setLoading] = useState(true);
    const [savingValue, setSaving] = useState(false);
    const [assetCreated, setAssetCreated] = useState(false);
    const [asset, setAsset] = useState(assetModel);
    const isUserDirty = useDirtyData(originalSettings, asset);
    const [assetTypes, setAssetTypes] = useState([]);
    const [assetTypeSubtypes, setAssetTypeSubtypes] = useState([]);
    const [assetOwnerships, setAssetOwnerships] = useState([]);
    const [tachographRules, setTachographRules] = useState([]);
    const [registrationType, setRegistrationType] = useState("");
    const [showLocationsAsHierarachy, setShowLocationsAsHierarachy] = useState(true);
    const monthsInPastDefault = 240;
    const minDateDefault = moment().subtract(monthsInPastDefault, 'months').toDate();
    const [isInternalMaintenance, setIsInternalMaintenance] = useState(false);
    const [isAttachable, setIsAttachable] = useState(false);
    const [assetList, setAssetList] = useState([]);
    const [assetSearch, setAssetSearch] = useState('');
    const [loadingAssets, setLoadingAssets] = useState(false);
    const [totalRelatedAssetRecordCount, setTotalAssetRecordCount] = useState(0);
    const [assetTypeSelected, setAssetTypeSelected] = useState(false);
    const [showDetrNumber, setShowDetrNumber] = useState(false);
    const [defaultAssetStatus, setDefaultAssetStatus] = useState(null);

    const defaultLocationList = {
        location: [{
            id: 0,
            type: "group",
            expanded: true,
            name: "All locations",
            checked: true,
            items: []
        }],
        hierarchy: []
    };

    const [tachoVisLocationList, setTachoVisLocationList] = useState({...defaultLocationList});
    const [visLocationList, setVisLocationList] = useState({...defaultLocationList});
    // the tachoLocationList and tachoVisLocationList will load into this based on the chosen asset type. Defaults to tachoLocationList
    const [activeLocationList, setActiveLocationList] = useState({...defaultLocationList});
    const [selectedLocations, setSelectedLocations] = useState([]);    

    const requiredAction = ["Fleet:Add", "FleetPCN:Add", "FleetVIS:Add"];
    const maintenanceBusinessArea = "2";
    const maxAssets = 50;
    const manageMyFleetArea = "MMF";

    useEffect(() => {
        initialLoad();
    }, [businessArea, area]);

    function initialLoad() {

        axios.all([
            AssetService.getAssetOptions(),
            UserService.getLoggedInUserLocationListForMultipleBusinessAreas([1, 2]),
            UserService.getLoggedInUserLocationListForMultipleBusinessAreas([2]),
            LocationService.getLocationBusinessAreas(), 
            UserService.getLoggedInUser()
        ])
        .then(axios.spread(function (
            optionsResponse, 
            tachoVisLocationPermissionsResponse, 
            visLocationPermissionsResponse, 
            businessAreasResponse, 
            userDetails
        ) {
            var activeStatus = optionsResponse.assetStatuses.find(x => x.name.toLowerCase() === 'active');
            setDefaultAssetStatus(activeStatus ?? null);
            var internalMaintenance = userDetails.isInternal && 
                (
                    businessArea === maintenanceBusinessArea || 
                    area === manageMyFleetArea
                );
            var assetOwnership = {};
            var analysisRules = null;

            //default values if internal maintenance
            if (internalMaintenance) {

                var owned = optionsResponse.assetOwnerships.filter(x => x.name === "Owned");
                var eu = optionsResponse.analysisRules.filter(x => x.name === "EU");

                assetOwnership = owned.length > 0 ? owned[0] : {};
                analysisRules = eu.length > 0 ? eu[0] : null;
                setRegistrationType("registration");
            }

            setIsInternalMaintenance(internalMaintenance);

            var clonedLocationSettings = {
                ...asset.assetLocation,
                assetStatus: activeStatus,
                assetOwnership: assetOwnership
            };

            let updatedAsset = {
                ...asset,
                analysisRules: analysisRules,
                assetLocation: clonedLocationSettings
            };

            setAsset(updatedAsset);
            setOriginalSettings(updatedAsset);

            var businessAreaValue = businessAreasResponse.find(x => x.locationBusinessAreaId === parseInt(businessArea));
            let mappedAssetTypes = optionsResponse.assetTypes.filter(x => x.publicAllowed);

            if (businessAreaValue != null) {
                mappedAssetTypes = optionsResponse.assetTypes.filter(x => 
                    x.publicAllowed && 
                    businessAreaValue != null && 
                    (
                        (businessAreaValue.businessArea.toUpperCase() === "VIS") || 
                        (businessAreaValue.businessArea.toUpperCase() !== "VIS" && !x.visOnly)
                    ));
            }

            mappedAssetTypes = mapAssetTypes(mappedAssetTypes);

            setAssetTypes(mappedAssetTypes);
            setAssetOwnerships(optionsResponse.assetOwnerships);
            setTachographRules(optionsResponse.analysisRules);

            setVisLocationList(createLocationObject(visLocationPermissionsResponse));

            const defaultLocationObject = createLocationObject(tachoVisLocationPermissionsResponse)
            setTachoVisLocationList(defaultLocationObject);
            setActiveLocationList(defaultLocationObject);

            setLoading(false);
        }));
    }

    function getAssetWithDefaultStatus(assetToUpdate) {
        if (assetToUpdate.assetLocation?.assetStatus?.id || !defaultAssetStatus?.id) {
            return assetToUpdate;
        }

        return {
            ...assetToUpdate,
            assetLocation: {
                ...assetToUpdate.assetLocation,
                assetStatus: defaultAssetStatus
            }
        };
    }

    function createLocationObject(locations) {
        const clonedLocationsObject = HelperFunctions.deepClone(defaultLocationList);
    
        clonedLocationsObject.location[0].items = locations.map(location => { 
            return { 
                ...location, 
                type: "item"
            }});

        return clonedLocationsObject;
    }

    function mapAssetTypes(optionAssetTypes) {
        let mappedAssetTypes = [];

        optionAssetTypes.forEach(optionAssetType => {
            optionAssetType.subtypeId = null;

            mappedAssetTypes.push(optionAssetType);
        });

        return mappedAssetTypes;
    }

    function onAssetTypeChange(type) {
        var typeName = type.name.toLowerCase();

        if (typeName === asset.assetType.name) {
            return;
        }

        var displayConfig = assetTypeDisplay.find(a => a.assetTypeId === type.id);
        setShowDetrNumber(displayConfig?.displayDetrNumber === true);

        var clonedAssets = {
            ...asset,
            assetType: type,
            registration: null,
            chassisNumber: null,
            tankNo: null,
            serialNumber: null,
            detrNumber: null,
            subtypeId: null
        };

        if (isAttachable !== type.isAttachable) {
            clonedAssets.linkedVehicleId = null;
            clonedAssets.relatedAssets = [];
        }

        setAsset(clonedAssets);

        setAssetTypeSelected(true);
        setIsAttachable(type.isAttachable);

        setAssetTypeSubtypes(type.subtypes ? type.subtypes : []);

        var regType = type.registration.toLowerCase();
        if (regType !== registrationType) {
            setRegistrationType(regType);
        }
 
        setActiveLocationList(type.visOnly ? visLocationList : tachoVisLocationList);
    }

    useEffect(() => {
        setSelectedLocations([]);
    }, [activeLocationList]);

    function onAssetTypeSubtypeChange(subtype) {
        if (subtype.id === asset.assetType.subtypeId) {
            return;
        }

        var clonedAsset = {
            ...asset,
            assetType: {
                ...asset.assetType,
                subtypeId: subtype.id
            }
        };

        setAsset(clonedAsset);
    }

    function onLinkedVehicleSelect(item) {
        setAsset({
            ...asset,
            linkedVehicleId: item.id
        });
    }

    function formatAssets(assets) {
        return assets.map(assetToMap => {
            return {
                id: assetToMap.assetId,
                name: assetToMap.assetRegistration
            };
        });
    }

    function searchAsset(listSize, pageNumber, searchText) {

        if (isAttachable == null) {
            return;
        }

        setLoadingAssets(true);

        const assetsRequest = {
            pageNumber: pageNumber,
            searchTerm: searchText,
            requestCount:listSize,
            filters: [{ key: "ISATTACHABLE", value: !isAttachable ? "YES" : "NO" }]
        };

        AssetService.searchAssets(assetsRequest).then(pagedAssets => {
            setAssetList(formatAssets(pagedAssets.data));
            setTotalAssetRecordCount(pagedAssets.totalCount);
            setLoadingAssets(false);
            }, (error) => {
                NotificationToaster.show(Intent.DANGER, "Could not load settings. Please refresh the page.");
            });
    }

    useEffect(function () {
        if (assetSearch.length > 0) {
            searchAsset(maxAssets, 1, assetSearch);
        }
    }, [assetSearch, isAttachable])

    function onAssetSearchTextChange(event) {
        setAssetSearch(event);
    }

    function onOwnershipChange(ownership) {

        var clonedLocationSettings = {
            ...asset.assetLocation,
            assetOwnership: ownership
        };

        setAsset({
            ...asset,
            assetLocation: clonedLocationSettings
        });
    }

    function onRulesetChange(ruleset) {
        setAsset({
            ...asset,
            analysisRules: ruleset
        });
    }

    function onRegistrationChange(event) {
        setAsset({
            ...asset,
            registration: event.target.value.toUpperCase()
        });
    }

    function onChassisNumberChange(event) {
        setAsset({
            ...asset,
            chassisNumber: event.target.value.toUpperCase()
        });
    }

    function onSerialNumberChange(event) {
        setAsset({
            ...asset,
            serialNumber: event.target.value.toUpperCase()
        });
    }

    function onTankNumberChange(event) {
        setAsset({
            ...asset,
            tankNo: event.target.value.toUpperCase()
        });
    }

    function onDetrNumberChange(event) {
        setAsset({
            ...asset,
            detrNumber: event.target.value
        });
    }

    function onStartDateChange(item) {
        var clonedLocationSettings = {
            ...asset.assetLocation,
            startDate: item ? moment(item).format("YYYY-MM-DD") : null
        };

        setAsset({
            ...asset,
            assetLocation: clonedLocationSettings
        });
    }

    function onLocationSelect(locations) {

        var lastLocation = locations.length > 0 ? locations.pop() : null;
        var lastLocationProperty = lastLocation == null ? {} : { id: lastLocation };

        var clonedLocationSettings = {
            ...asset.assetLocation,
            location: lastLocationProperty
        };

        setAsset({
            ...asset,
            assetLocation: clonedLocationSettings
        });

        setSelectedLocations(lastLocation == null ? [] : [lastLocation]);
    }

    function onRelatedAssetChange(item, isChecked) {
        if (isChecked) {
            const relatedAssetsCloned = [...asset.relatedAssets];
            relatedAssetsCloned.push({
                ...item,
                assetId: item.id
            });

            setAsset({
                ...asset,
                relatedAssets: relatedAssetsCloned
            });

        } else {
            const selectedLocationIndex = asset.relatedAssets.findIndex(x => x.id === item.id);
            onRemove(selectedLocationIndex, 'relatedAssets');
        }
    }

    function onRemove(index, listName) {
        var temp = [...asset[listName]];
        temp.splice(index, 1);

        var updatedAssetSettings = {
            ...asset
        };

        updatedAssetSettings[listName] = temp;
        setAsset(updatedAssetSettings);
    }

    function onRelatedAssetsPageChange(listSize, pageNumber, debouncedSearchTerm) {
        setLoadingAssets(true);
        searchAsset(listSize, pageNumber, debouncedSearchTerm);
    }
    function setUseHierarchy() {
        setShowLocationsAsHierarachy(previous => !previous);
    }


    function saveAsset() {
        setSaving(true);

        const assetToSave = getAssetWithDefaultStatus(asset);

        AssetService.createAsset(assetToSave).then(
            (assetId) => {
                setSaving(false);
                setAssetCreated(true);
                setAsset(assetModel);
                setOriginalSettings(assetModel);
                setSelectedLocations([]);
            },
            (error) => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, error);
            }
        );
    }

    function resetComponent() {
        setAsset(assetModel);
        setOriginalSettings(assetModel);
        initialLoad();
        setSaving(false);
        setAssetCreated(false);
    }

    useEffect(validateModel, [asset])

    function validateModel() {
        let rules = [{ fieldName: "assetLocation.location", required: true }, { fieldName: "assetLocation.assetOwnership", required: true }];

        if (!isInternalMaintenance) {
            rules.push({ fieldName: "assetType", required: true },{ fieldName: "assetLocation.startDate", required: true });
        }
        switch (registrationType) {

            case "registration":
                rules.push({ fieldName: "registration", required: true, type: "alphanumeric" })
                break;
            case "serial":
                rules.push({ fieldName: "serialNumber", required: true })
                break;
            case "chassis":
                rules.push({ fieldName: "chassisNumber", required: true })
                break;
            case "tank":
                rules.push({ fieldName: "tankNo", required: true })
                break;
            default:
                rules.push({ fieldName: "registration", required: true, type: "alphanumeric" })
                break;
        }

        validate(rules, asset);
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (

                <div className="row">
                    <Breadcrumbs items={breadcrumbs} />
                    <ShowHide
                        evaluator={!assetCreated}
                        show={(
                            <div>
                                <h1 id="header-new-asset">Asset Details</h1>

                                <FormSelect
                                    id="field-asset-type"
                                    disabled={savingValue}
                                    items={assetTypes}
                                    onItemSelect={onAssetTypeChange}
                                    placeholder="Select an asset type"
                                    headingText="Asset type:"
                                    selectedValue={asset.assetType.id}
                                    loading={loading}
                                    dangerHelperText={errors.assetType}
                                ></FormSelect>

                                <ShowHide 
                                    evaluator={assetTypeSelected && assetTypeSubtypes && assetTypeSubtypes.length > 0}
                                    show={(
                                        <FormSelect
                                            id="field-asset-type-subtype"
                                            disabled={savingValue}
                                            items={assetTypeSubtypes}
                                            onItemSelect={onAssetTypeSubtypeChange}
                                            placeholder="Select an asset type subtype"
                                            headingText="Asset type subtype:"
                                            selectedValue={asset.assetType?.subtypeId}
                                            loading={loading}
                                        ></FormSelect>
                                    )}
                                />

                                <ShowHide
                                    evaluator={registrationType === "registration" || (registrationType === "" && isInternalMaintenance)}
                                    show={(
                                        <FormTextInput
                                            id="field-asset-registration"
                                            loading={loading}
                                            value={asset.registration ? asset.registration : ""}
                                            headingText="Registration:"
                                            onChange={onRegistrationChange}
                                            disabled={savingValue}
                                            dangerHelperText={errors.registration}
                                        >
                                        </FormTextInput>
                                    )}
                                />

                                <ShowHide
                                    evaluator={registrationType === "serial"}
                                    show={(
                                        <FormTextInput
                                            id="field-asset-serial"
                                            loading={loading}
                                            value={asset.serialNumber ? asset.serialNumber : ""}
                                            headingText="Serial Number:"
                                            onChange={onSerialNumberChange}
                                            disabled={savingValue}
                                            dangerHelperText={errors.serialNumber}
                                        >
                                        </FormTextInput>
                                    )}
                                />

                                <ShowHide
                                    evaluator={registrationType === "tank"}
                                    show={(
                                        <FormTextInput
                                            id="field-asset-tank-number"
                                            loading={loading}
                                            value={asset.tankNo ? asset.tankNo : ""}
                                            headingText="Tank Number:"
                                            onChange={onTankNumberChange}
                                            disabled={savingValue}
                                            dangerHelperText={errors.tankNo}
                                        >
                                        </FormTextInput>
                                    )}
                                />

                                <ShowHide
                                    evaluator={registrationType === "chassis"}
                                    show={(
                                        <FormTextInput
                                            id="field-asset-chassis"
                                            loading={loading}
                                            value={asset.chassisNumber ? asset.chassisNumber : ""}
                                            headingText="Chassis Number:"
                                            onChange={onChassisNumberChange}
                                            disabled={savingValue}
                                            dangerHelperText={errors.chassisNumber}
                                        >
                                        </FormTextInput>
                                    )}
                                />
                                <ShowHide
                                    evaluator={showDetrNumber}
                                    show={(
                                        <FormTextInput
                                            id="field-asset-detr-number"
                                            loading={loading}
                                            value={asset.detrNumber ? asset.detrNumber : ""}
                                            headingText="DETR Number:"
                                            onChange={onDetrNumberChange}
                                            disabled={saving}
                                            maxLength={255}
                                            helperText="For example, C236718 or A787121 or NI/049095/06. This information, when added correctly will allow MOT results to be displayed for the trailer"
                                        >
                                        </FormTextInput>
                                    )}
                                />

                                <FormLocationSelector
                                    headingText={`Location asset belongs to:`}
                                    dangerHelperText={errors["assetLocation.location"]}
                                    businessArea={""}
                                    loading={loading}
                                    selectedLocations={selectedLocations}
                                    setSelectedLocations={onLocationSelect}
                                    useHierarchy={showLocationsAsHierarachy}
                                    setUseHierarchy={setUseHierarchy}
                                    useLocationDefaults={false}
                                    useCustomLocations
                                    customLocations={activeLocationList}
                                    locationDefaultsToTrue
                                    singleSelection
                                />

                                <FormSelect
                                    id="field-asset-ownership"
                                    disabled={savingValue}
                                    items={assetOwnerships}
                                    onItemSelect={onOwnershipChange}
                                    placeholder="Select an ownership status"
                                    headingText="Asset ownership status:"
                                    selectedValue={asset.assetLocation.assetOwnership.id}
                                    loading={loading}
                                    dangerHelperText={errors["assetLocation.assetOwnership"]}
                                ></FormSelect>

                                <FormSelect
                                    id="field-asset-ruleset"
                                    disabled={savingValue}
                                    items={tachographRules}
                                    onItemSelect={onRulesetChange}
                                    placeholder="Select a ruleset"
                                    headingText="Tachograph rules:"
                                    selectedValue={asset.analysisRules?.id}
                                    loading={loading}
                                ></FormSelect>

                                <FormDateInput
                                    id="field-asset-start-date"
                                    disabled={savingValue}
                                    headingText="Start date:"
                                    onChange={onStartDateChange}
                                    value={asset.assetLocation.startDate}
                                    loading={loading}
                                    helperText="This is the date from which we will start analysis"
                                    dangerHelperText={errors.startDate}
                                    minDate={minDateDefault}
                                ></FormDateInput>

                                <ShowHide
                                    evaluator={assetTypeSelected}
                                    show={(
                                        <ShowHide
                                            evaluator={isAttachable}
                                            show={(
                                                <FormSuggest
                                                    onQueryChange={onAssetSearchTextChange}
                                                    items={assetList}
                                                    selectedValue={asset.linkedVehicleId}
                                                    loading={loading}
                                                    onItemSelect={onLinkedVehicleSelect}
                                                    onFilter={() => { }}
                                                    headingText="Linked asset:"
                                                    helperText="Select the asset to link this asset to."
                                                    placeholder="Select parent asset"
                                                    dangerHelperText={errors.linkedVehicleId}
                                                    disabled={savingValue || isAttachable === null}
                                                    clearSelection={true}
                                                />
                                            )}
                                            hide={(
                                                <FormPagedCheckboxList
                                                    id="related-assets"
                                                    onPageChange={onRelatedAssetsPageChange}
                                                    headingText="Select the assets related to this asset:"
                                                    items={assetList}
                                                    totalItemCount={totalRelatedAssetRecordCount}
                                                    onItemSelect={onRelatedAssetChange}
                                                    selectedItems={asset.relatedAssets}
                                                    dangerHelperText={errors.relatedAssets}
                                                    placeholder="Search assets"
                                                    loading={loading}
                                                    loadingPage={loadingAssets}
                                                    disabled={disabled}
                                                    saving={saving}
                                                    noDataMessage="No assets found."
                                                ></FormPagedCheckboxList>
                                            )}

                                        />
                                    )}
                                />

                                <AlertUnsavedData
                                    isDirty={isUserDirty}>
                                </AlertUnsavedData>

                                <ButtonSave disabled={savingValue} simpleDisabled={!isValid} onClick={saveAsset} />

                            </div>
                        )}
                        hide={(
                            <div>
                                <div className="spacer-bottom" id="create-success-icon">
                                    <Icon icon="tick-circle" size={40} intent={Intent.SUCCESS} />
                                </div>
                                <h2 id="create-success-name">Asset has been added successfully</h2>
                                <div className="button-row" id="create-success-buttons">
                                    <LinkButton intent="primary" text={breadcrumbs[0].title} href={breadcrumbs[0].location} externalLink={breadcrumbs[0].externalLink ?? false} />
                                    <Button intent="primary" text="Add new asset" id="new-asset" onClick={resetComponent} />
                                </div>
                            </div>
                        )}
                    ></ShowHide>

                </div>

            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

AssetCreateSettings.propTypes = {
    businessArea: PropTypes.string,
    breadcrumbs: PropTypes.array,
    area: PropTypes.string
};
